import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { FaUser, FaStar, FaMedal, FaCalendarAlt } from 'react-icons/fa';
import './Community.css';
import { formatTimeAgo } from '../../utils/communityUtils';
import CommunityBadge from './shared/CommunityBadge';

export default function CommunityProfile() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/profile/${userId}`);
        const data = await res.json();
        if (data.success) {
          setProfile(data.profile);
        }
      } catch (error) {
        console.error("Error fetching profile", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [userId]);

  if (loading) {
    return (
      <div className="community-container d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="community-container text-center py-5">
        <h2 className="text-white">Profile not found</h2>
        <Link to="/community" className="community-link mt-3 d-inline-block">Return to Community Home</Link>
      </div>
    );
  }

  const joinDate = profile.joinedCommunityAt 
    ? new Date(profile.joinedCommunityAt).toLocaleDateString()
    : 'Recently';

  return (
    <div className="community-container">
      <Container>
        <Row className="justify-content-center">
          <Col lg={8}>
            <div className="community-card p-4 p-md-5">
              <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-4 mb-5 pb-4 border-bottom border-dark">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white"
                  style={{ width: '100px', height: '100px', fontSize: '40px' }}
                >
                  {profile.displayName ? profile.displayName.charAt(0).toUpperCase() : <FaUser />}
                </div>
                
                <div className="text-center text-md-start flex-grow-1">
                  <h2 className="text-white mb-2">{profile.displayName || 'BBR User'}</h2>
                  
                  <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start mb-3">
                    {(profile.communityRoles || []).map((role, idx) => (
                      <CommunityBadge key={idx} role={role} />
                    ))}
                    {profile.verifiedOperatorStatus?.status === 'approved' && !profile.communityRoles?.includes('Verified Bass Boat Operator') && (
                      <CommunityBadge role="Verified Bass Boat Operator" />
                    )}
                  </div>
                  
                  <p className="text-muted mb-3">
                    {profile.bio || 'This user has not set a bio yet.'}
                  </p>
                  
                  <div className="d-flex gap-4 justify-content-center justify-content-md-start text-muted small">
                    <span><FaStar className="me-1 text-warning" /> {profile.reputation || 0} Rep</span>
                    <span><FaCalendarAlt className="me-1" /> Joined {joinDate}</span>
                  </div>
                </div>
              </div>
              
              <Row>
                <Col md={6} className="mb-4">
                  <h4 className="text-white mb-3">Badges</h4>
                  {profile.badges && Object.keys(profile.badges).length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {Object.values(profile.badges).map((badge, idx) => (
                        <Badge key={idx} bg="dark" border="secondary" className="p-2 border">
                          <FaMedal className="me-1 text-warning" /> {badge.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted small">No badges earned yet.</p>
                  )}
                </Col>
                
                <Col md={6}>
                  <h4 className="text-white mb-3">Followed Lakes</h4>
                  {profile.followedLakes && profile.followedLakes.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {profile.followedLakes.map((lake, idx) => (
                        <Link key={idx} to={`/community/lakes/${lake}`} className="badge bg-secondary text-decoration-none p-2">
                          {lake.replace('lake_', '').replace(/_/g, ' ').toUpperCase()}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted small">Not following any lakes.</p>
                  )}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
